import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Screen } from '../../app/Frame';
import { useMeContext, useWritable } from '../../app/MeContext';
import { useScreen, useSheet, useToast } from '../../app/shell-context';
import { ApiError, NetworkError } from '../../api/client';
import { formatDateTime, formatMinutes, weekdayRu, type TimeFormat } from '../../lib/time';
import { plural, pluralWithCount } from '../../lib/plural';
import { Avatar, Notice, Pill } from '../../ui/parts';
import {
  useAdminChild, useAdminClients, useAdminDirections, useAdminReferrals, useAdminSpecialists,
  useAssign, useCreateFamily, useDeclineReferral, useInviteLink, useRequestSeries, useUnlinkClient,
  useUpdateFamily,
} from '../api';
import type { AdminReferral, Family } from '../types';
import type { TeamMember } from '../../api/types';
import type { Series } from '../../staff/types';

/**
 * Вкладка «Клиенты»: заявки и клиенты организации. Единица раздела — клиент: за
 * ним закреплён специалист, на него ведётся расписание, на него заводится карточка.
 *
 * В текстах говорим «клиент» и «контактное лицо». Организации бывают разные, и
 * «ребёнок» с «родителем» верны только в детских центрах: у остальных они
 * превращают интерфейс в чужой. Тот, кто получает услугу, — клиент; тот, кому
 * пишет бот и уходят напоминания, — контактное лицо, и это может быть он сам.
 */
export function Clients() {
  const writable = useWritable();
  const navigate = useNavigate();
  const sheet = useSheet();
  const [query, setQuery] = useState('');
  const [search, setSearch] = useState('');
  const clients = useAdminClients(search);
  const referrals = useAdminReferrals();
  const specialists = useAdminSpecialists();

  useScreen('Клиенты', null);

  const list = clients.data?.pages.flatMap((p) => p.items) ?? [];
  const total = clients.data?.pages[0]?.total ?? 0;
  const refs = referrals.data ?? [];
  const paused = new Set((specialists.data ?? []).filter((s) => s.status === 'paused').map((s) => s.id));

  // Поиск отправляем с задержкой: администратор печатает фамилию целиком,
  // а не по букве на запрос.
  useEffect(() => {
    const id = window.setTimeout(() => setSearch(query.trim()), 300);
    return () => clearTimeout(id);
  }, [query]);

  const empty = !clients.isPending && list.length === 0;

  return (
    <Screen>
      {refs.length ? (
        <div>
          <p className="eyebrow">Заявки от клиентов и специалистов</p>
          <div className="rows" style={{ marginTop: 10 }}>
            {refs.map((r) => <ReferralRow key={r.id} referral={r} />)}
          </div>
        </div>
      ) : null}

      <div className="field">
        <input
          type="search"
          placeholder="Клиент, контакт или телефон"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      {writable ? (
        <button className="add" onClick={() => navigate('/admin/clients/add')}>＋ Добавить клиента</button>
      ) : null}

      {clients.isPending ? <p className="lead">Загружаем…</p> : null}

      <div className="rows">
        {list.map((f) => {
          const status = familyStatus(f, paused);
          return (
            <button className="rw" key={f.id} onClick={() => sheet.open(<FamilySheet family={f} paused={paused} />)}>
              <Avatar name={f.name} small />
              <span className="rw-body">
                <b>{childLine(f)}</b>
                <span>{teamLine(f)}</span>
                <span>{f.parent_name} · {formatPhone(f.phone)}</span>
                {status ? (
                  <span className="chips">
                    <Pill kind={status.tone}>{status.label}</Pill>
                  </span>
                ) : null}
              </span>
            </button>
          );
        })}
      </div>

      {empty && search ? (
        <p className="lead">Ничего не нашли по «{search}» — попробуйте фамилию клиента или телефон контактного лица</p>
      ) : null}

      {empty && !search ? (
        <div>
          <p className="lead" style={{ fontWeight: 600 }}>Здесь появятся клиенты организации</p>
          <p className="lead" style={{ marginTop: 6 }}>
            Клиент добавляется сам, когда открывает бота по приглашению.
            Если он уже получает услуги — добавьте его вручную.
          </p>
        </div>
      ) : null}

      {clients.hasNextPage ? (
        <button className="add" disabled={clients.isFetchingNextPage} onClick={() => void clients.fetchNextPage()}>
          {clients.isFetchingNextPage ? 'Загружаем…' : `Показать ещё · осталось ${total - list.length}`}
        </button>
      ) : null}

    </Screen>
  );
}

/** Имя и возраст: возраст цифрой со словом, иначе «8» читается как номер по списку. */
function childLine(f: Family): string {
  return f.birth_year ? `${f.name} · ${age(f.birth_year)}` : f.name;
}

/** Направление и специалист. Третий и далее сворачиваются: строка не должна переноситься. */
function teamLine(f: Family): string {
  if (!f.team.length) return 'Специалист не закреплён';
  const shown = f.team.slice(0, 2)
    .map((t) => `${t.direction_title} — ${t.specialist_name.split(' ')[0]}`)
    .join(', ');
  const rest = f.team.length - 2;
  return rest > 0 ? `${shown} и ещё ${rest}` : shown;
}

/** Единый формат телефона: в списке цифры должны стоять в колонку. */
function formatPhone(raw: string): string {
  const d = raw.replace(/\D/g, '');
  const n = d.length === 11 && d.startsWith('8') ? `7${d.slice(1)}` : d;
  if (n.length !== 11) return raw;
  return `+${n[0]} ${n.slice(1, 4)} ${n.slice(4, 7)}-${n.slice(7, 9)}-${n.slice(9)}`;
}

/**
 * Специалисты, у которых расписания ещё нет. Серия привязана к специалисту, а не к
 * клиенту: одна серия у логопеда не значит, что нейропсихолог своё расписание выставил.
 */
const awaitingSeries = (f: Family): TeamMember[] =>
  f.team.filter((t) => !f.series.some((s) => s.specialist_id === t.specialist_id));

type ChildStatus = { key: string; label: string; tone: 'off' | 'await' | 'done'; hint: string };

/**
 * Ноль или один статус на строку, и только тот, что требует действия: норма ярлыка
 * не получает, иначе статусы перестают работать как сигнал. Порядок в списке —
 * порядок приоритета: без специалиста приглашение почти бесполезно.
 *
 * Цвет отвечает на вопрос «чей ход», а не «насколько всё плохо»:
 *   красный — не работает вообще, записывать некуда;
 *   золотой — ход администратора, он может закрыть статус сам;
 *   серый   — ждём специалиста или контактное лицо, администратору делать нечего.
 * Поэтому «Расписание не задано» серое, хотя и требует внимания: выставить
 * расписание может только специалист, админу остаётся попросить.
 */
function familyStatus(f: Family, pausedSpecialists: Set<number>): ChildStatus | null {
  if (!f.team.length) {
    return { key: 'no_specialist', label: 'Нет специалиста', tone: 'off', hint: 'Закрепите специалиста, иначе клиенту доступна только первичная услуга.' };
  }
  if (f.team.length && f.team.every((t) => pausedSpecialists.has(t.specialist_id))) {
    return { key: 'paused', label: 'Записи приостановлены', tone: 'off', hint: 'Специалист на паузе, записать к нему нельзя.' };
  }
  if (!f.tg_id && !f.invite_sent_at) {
    return {
      key: 'not_invited', label: 'Приглашение не отправлено', tone: 'await',
      hint: `Скопируйте ссылку и отправьте ${f.parent_name || 'контактному лицу'}: по ней откроется бот с расписанием.`,
    };
  }
  const waiting = awaitingSeries(f);
  if (waiting.length) {
    return {
      key: 'no_schedule',
      label: 'Расписание не задано',
      tone: 'done',
      hint: waiting.length === 1
        ? 'Специалист ещё не выставил расписание, регулярных записей нет.'
        : `Расписание не выставили ${pluralWithCount(waiting.length, 'специалист', 'специалиста', 'специалистов')}, регулярных записей нет.`,
    };
  }
  if (!f.tg_id) {
    return { key: 'not_in_bot', label: 'Бот не открыт', tone: 'done', hint: 'Ссылка отправлена, но в бота по ней ещё не заходили.' };
  }
  return null;
}

function ReferralRow({ referral }: { referral: AdminReferral }) {
  const sheet = useSheet();
  const directions = useAdminDirections();
  const dir = directions.data?.find((d) => d.id === referral.direction_id);

  return (
    <div className="rw">
      <span className="rw-body">
        <b>{referral.child_name} → {dir ? dir.title.toLowerCase() : 'категория не выбрана'}</b>
        <span>
          {referral.from_specialist_id ? 'от коллеги' : 'от клиента'} · {referral.note || 'без комментария'}
        </span>
      </span>
      {referral.specialist_reply === 'accepted' ? <Pill kind="paid">специалист готов</Pill> : null}
      {referral.specialist_reply === 'declined' ? <Pill kind="off">специалист отказался</Pill> : null}
      <button
        className="btn solid"
        style={{ flex: '0 0 auto', padding: '8px 12px' }}
        onClick={() => sheet.open(<ReferralSheet referral={referral} />)}
      >
        Назначить
      </button>
    </div>
  );
}

function ReferralSheet({ referral }: { referral: AdminReferral }) {
  const sheet = useSheet();
  const toast = useToast();
  const clients = useAdminClients();
  const decline = useDeclineReferral();

  const family = clients.data?.pages.flatMap((p) => p.items).find((f) => f.id === referral.child_id);

  return (
    <>
      <h3>Заявка · {referral.child_name}</h3>
      <p>{referral.from_specialist_id ? 'От коллеги' : 'От клиента, через приложение'}.</p>
      <div className="kv">
        <div>
          <span className="k">Запрос</span>
          <span className="v" style={{ fontWeight: 500, textAlign: 'right', maxWidth: '60%' }}>{referral.note || 'не указан'}</span>
        </div>
        <div>
          <span className="k">Контактное лицо</span>
          <span className="v">{referral.parent_name} · {referral.phone}</span>
        </div>
        {referral.specialist_reply ? (
          <div>
            <span className="k">Ответ специалиста</span>
            <span className="v">{referral.specialist_reply === 'accepted' ? 'готов взять' : 'не его профиль'}</span>
          </div>
        ) : null}
      </div>

      <div className="row-actions">
        <button
          className="btn ghost-danger"
          disabled={decline.isPending}
          onClick={() =>
            decline.mutate(referral.id, {
              onSuccess: () => {
                toast('Заявка отклонена');
                sheet.close();
              },
              onError: (e) => toast(e instanceof ApiError ? e.message : 'Не получилось'),
            })
          }
        >
          Отклонить
        </button>
        <button
          className="btn solid"
          disabled={!family}
          onClick={() => (family ? sheet.open(<AssignSheet family={family} />) : undefined)}
        >
          Закрепить специалиста
        </button>
      </div>
    </>
  );
}

/**
 * Карточка клиента. Порядок блоков — по частоте обращения, а не по модели данных:
 * специалист и контактное лицо выше расписания, их чаще ищут глазами.
 */
function FamilySheet({ family, paused }: { family: Family; paused: Set<number> }) {
  const writable = useWritable();
  const me = useMeContext();
  const sheet = useSheet();
  const toast = useToast();
  const invite = useInviteLink();
  const { unassign } = useAssign();
  const requestSeries = useRequestSeries();
  const card = useAdminChild(family.id);

  const status = familyStatus(family, paused);
  const waiting = awaitingSeries(family);
  const upcoming = card.data?.upcoming ?? [];
  const history = card.data?.history ?? [];

  /**
   * Просим расписание у каждого, у кого его нет. Раньше запрос уходил первому в списке —
   * с командой из троих это лотерея, а администратор был уверен, что попросил всех.
   */
  const askForSeries = () => {
    const asked = waiting;
    void Promise.allSettled(
      asked.map((t) => requestSeries.mutateAsync({ childId: family.id, specialistId: t.specialist_id })),
    ).then((res) => {
      const done = res.flatMap((r, i) => (r.status === 'fulfilled' ? [{ t: asked[i], notified: r.value.notified }] : []));
      if (!done.length) return toast('Не получилось');
      const sent = done.filter((d) => d.notified);
      const silent = done.filter((d) => !d.notified);
      // Без привязанного бота просьба оседает в журнале — говорить «попросили» нельзя.
      if (!sent.length) {
        return toast(silent.length === 1
          ? `${silent[0].t.specialist_name}: бот не подключён, просьба не дойдёт`
          : 'Ни у кого из специалистов не подключён бот, просьба не дойдёт');
      }
      toast((sent.length === 1
        ? `Попросили ${sent[0].t.specialist_name} выставить расписание`
        : `Попросили ${sent.length} специалистов выставить расписание`)
        + (silent.length ? `, ${pluralWithCount(silent.length, 'специалист', 'специалиста', 'специалистов')} без бота` : ''));
    });
  };

  const copyInvite = () => invite.mutate(family.client_id, {
    onSuccess: (res) => {
      void navigator.clipboard?.writeText(`https://${res.link}`);
      toast(`Ссылка скопирована — отправьте её ${family.parent_name}`);
    },
    onError: (e) => toast(e instanceof ApiError && e.is('BOT_NOT_CONFIGURED')
      ? 'У организации не настроен бот — ссылку собрать не из чего'
      : 'Не получилось получить ссылку'),
  });

  /**
   * Главное действие — то, что закрывает текущий статус, и стоит рядом с ним.
   * Остальные действия второстепенны и живут внизу; то, что уже вынесено наверх,
   * внизу не повторяется.
   */
  const primary = !writable || !status ? null
    : status.key === 'no_specialist'
      ? { kind: 'assign' as const, label: 'Закрепить специалиста', busy: false, onClick: () => sheet.open(<AssignSheet family={family} />) }
    : status.key === 'not_invited' || status.key === 'not_in_bot'
      ? { kind: 'invite' as const, label: 'Скопировать приглашение', busy: invite.isPending, onClick: copyInvite }
    : status.key === 'no_schedule'
      ? { kind: 'series' as const, label: 'Попросить расписание', busy: requestSeries.isPending, onClick: askForSeries }
    : null;
  const showInvite = writable && !family.tg_id && primary?.kind !== 'invite';
  const showSeries = writable && waiting.length > 0 && primary?.kind !== 'series';
  const noRecords = !upcoming.length && !family.series.length && !history.length;

  return (
    <>
      <h3>{childLine(family)}</h3>
      {status ? (
        <div className="status-note">
          <span><Pill kind={status.tone}>{status.label}</Pill></span>
          <p className="lead">{status.hint}</p>
        </div>
      ) : null}
      {primary ? (
        <button className="btn solid" disabled={primary.busy} onClick={primary.onClick}>{primary.label}</button>
      ) : null}

      <p className="eyebrow">Специалисты</p>
      <div className="rows">
        {family.team.length ? (
          family.team.map((t) => (
            <div className="rw" key={t.direction_id}>
              <Avatar name={t.specialist_name} photo={t.specialist_photo} small />
              <span className="rw-body">
                <b>{t.specialist_name}</b>
                <span>
                  {t.direction_title}
                  {paused.has(t.specialist_id) ? ' · на паузе' : ''}
                  {waiting.some((w) => w.specialist_id === t.specialist_id) ? ' · расписание не задано' : ''}
                </span>
              </span>
              {writable ? (
                <button
                  className="btn ghost-danger"
                  style={{ flex: '0 0 auto', padding: '8px 12px' }}
                  disabled={unassign.isPending}
                  onClick={() => sheet.open(<UnassignSheet family={family} member={t} />)}
                >
                  Открепить
                </button>
              ) : null}
            </div>
          ))
        ) : (
          <p className="lead">Специалист не закреплён. До закрепления клиент сможет записаться только на первичную услугу.</p>
        )}
        {writable && primary?.kind !== 'assign' ? (
          <button className="add" onClick={() => sheet.open(<AssignSheet family={family} />)}>
            ＋ Закрепить специалиста
          </button>
        ) : null}
      </div>

      <p className="eyebrow">Контактное лицо</p>
      <div className="rows">
        <div className="rw">
          <span className="rw-body">
            <b>{family.parent_name}</b>
            <span>
              {formatPhone(family.phone)}
              {family.tg_id ? ' · в боте' : ' · ещё не в боте'}
            </span>
          </span>
          {writable && family.tg_id ? (
            <button
              className="btn ghost-danger"
              style={{ flex: '0 0 auto', padding: '8px 12px' }}
              onClick={() => sheet.open(<UnlinkParentSheet family={family} />)}
            >
              Отвязать
            </button>
          ) : null}
        </div>
      </div>

      <p className="eyebrow">Запрос</p>
      {family.note ? <p>{family.note}</p> : <p className="lead">Не указан</p>}

      <p className="eyebrow">Записи</p>
      {card.isPending ? (
        <p className="lead">Загружаем…</p>
      ) : noRecords ? (
        <p className="lead">Записей пока нет</p>
      ) : (
        <div className="rows">
          {upcoming.length ? (
            <>
              <p className="lead" style={{ fontWeight: 600 }}>Ближайшие</p>
              {upcoming.slice(0, 3).map((a) => (
                <div className="rw" key={a.id}>
                  <span className="rw-body">
                    <b>{formatDateTime(a.starts_at_utc, me.center.tz, me.center.timeFormat)}</b>
                    <span>{a.specialist_name} · {a.service_title}</span>
                  </span>
                </div>
              ))}
            </>
          ) : null}
          {family.series.length ? (
            <>
              <p className="lead" style={{ fontWeight: 600 }}>Расписание</p>
              {family.series.map((s) => (
                <div className="rw" key={s.id}>
                  <span className="rw-body">
                    <b>{seriesLine(s, me.center.timeFormat)}</b>
                    <span>{s.service_title} · {s.specialist_name} · {pluralWithCount(s.made, 'запись', 'записи', 'записей')}</span>
                  </span>
                </div>
              ))}
            </>
          ) : null}
          {history.length ? (
            <>
              <p className="lead" style={{ fontWeight: 600 }}>Прошедшие</p>
              {history.slice(0, 5).map((a) => (
                <div className="rw" key={a.id}>
                  <span className="rw-body">
                    <b>{formatDateTime(a.starts_at_utc, me.center.tz, me.center.timeFormat)}</b>
                    <span>{a.specialist_name} · {a.service_title} · {historyStatus(a.status)}</span>
                  </span>
                </div>
              ))}
            </>
          ) : null}
        </div>
      )}

      {showInvite ? (
        <button className="btn" disabled={invite.isPending} onClick={copyInvite}>Скопировать приглашение</button>
      ) : null}
      {showSeries ? (
        <button className="btn" disabled={requestSeries.isPending} onClick={askForSeries}>Попросить расписание</button>
      ) : null}
      <div className="row-actions">
        {writable ? (
          <button className="btn" onClick={() => sheet.open(<ClientEditSheet family={family} />)}>Изменить карточку</button>
        ) : null}
        <button className="btn" onClick={() => sheet.close()}>Закрыть</button>
      </div>
    </>
  );
}

/**
 * Правка карточки: клиент и его контактное лицо на одном экране.
 *
 * В базе это две строки и две ручки, но администратор правит не строки, а
 * карточку: имя записали с ошибкой, телефон сменился, запрос уточнили на первой
 * встрече. Уходит только изменившаяся половина — вписанный заново телефон
 * сервер считает неподтверждённым, и сбрасывать эту отметку из-за исправленной
 * буквы в имени нечестно.
 *
 * Мутации заведены здесь, а не приняты сверху: шторка открыта поверх карточки,
 * и колбэки чужой мутации выбросило бы вместе с ней.
 */
function ClientEditSheet({ family }: { family: Family }) {
  const sheet = useSheet();
  const toast = useToast();
  const { child, contact } = useUpdateFamily();

  const thisYear = new Date().getUTCFullYear();
  const [name, setName] = useState(family.name);
  const [age, setAge] = useState(family.birth_year ? String(thisYear - family.birth_year) : '');
  const [note, setNote] = useState(family.note);
  const [parent, setParent] = useState(family.parent_name);
  const [phone, setPhone] = useState(family.phone);

  const nameOk = name.trim().length > 1;
  const phoneOk = phone.replace(/\D/g, '').length >= 10;
  const ageOk = !age.trim() || (Number.isInteger(Number(age)) && Number(age) > 0 && Number(age) < 120);
  const ready = nameOk && parent.trim().length > 0 && phoneOk && ageOk;

  // Возраст показываем, а храним год рождения: иначе карточка старела бы только
  // при правке. Пустое поле — «возраст не знаем», а не «ноль лет».
  const birthYear = age.trim() ? thisYear - Number(age) : null;
  const childChanged = name.trim() !== family.name || birthYear !== family.birth_year || note.trim() !== family.note;
  const contactChanged = parent.trim() !== family.parent_name || phone.trim() !== family.phone;
  const busy = child.isPending || contact.isPending;

  const save = async () => {
    if (!childChanged && !contactChanged) return sheet.close();
    try {
      if (childChanged) {
        await child.mutateAsync({ childId: family.id, name: name.trim(), birthYear, note: note.trim() });
      }
      if (contactChanged) {
        await contact.mutateAsync({ clientId: family.client_id, parentName: parent.trim(), phone: phone.trim() });
      }
      toast('Карточка сохранена');
      sheet.close();
    } catch (e) {
      // Связь пропала на второй половине — первая уже сохранена, и повторное
      // «Сохранить» ничего не сломает: уйдёт только то, что ещё не совпало.
      toast(e instanceof NetworkError
        ? 'Не сохранили — связь пропала. Данные в форме остались, попробуйте ещё раз'
        : e instanceof ApiError ? e.message : 'Не получилось сохранить');
    }
  };

  return (
    <>
      <h3>Правка карточки</h3>

      <p className="eyebrow">Клиент</p>
      <div className="two">
        <div className="field">
          <label htmlFor="ceName">Имя и фамилия</label>
          <input id="ceName" type="text" value={name} onChange={(e) => setName(e.target.value)} />
          {name && !nameOk ? <span className="err">Без имени карточку не найти в списке</span> : null}
        </div>
        <div className="field">
          <label htmlFor="ceAge">Возраст</label>
          <input
            id="ceAge"
            type="number"
            inputMode="numeric"
            placeholder="—"
            value={age}
            onChange={(e) => setAge(e.target.value)}
          />
        </div>
      </div>
      <div className="field">
        <label htmlFor="ceNote">Запрос</label>
        <textarea id="ceNote" value={note} onChange={(e) => setNote(e.target.value)} />
        <span className="hint">Запрос видят специалисты, работающие с клиентом.</span>
      </div>

      <p className="eyebrow">Контактное лицо</p>
      <div className="field">
        <label htmlFor="ceParent">Имя</label>
        <input id="ceParent" type="text" value={parent} onChange={(e) => setParent(e.target.value)} />
      </div>
      <div className="field">
        <label htmlFor="cePhone">Телефон</label>
        <input id="cePhone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />
        <span className={phone && !phoneOk ? 'err' : 'hint'}>
          {phone && !phoneOk
            ? 'Проверьте номер — 11 цифр, начиная с 7'
            : family.tg_id
              ? 'Вход в приложение и напоминания привязаны к аккаунту, а не к номеру: смена телефона их не тронет.'
              : 'Используется для связи до подключения клиента к боту.'}
        </span>
      </div>

      <div className="row-actions">
        <button className="btn" onClick={() => sheet.close()}>Отмена</button>
        <button className="btn solid" disabled={!ready || busy} onClick={() => void save()}>
          {busy ? 'Сохраняем…' : 'Сохранить'}
        </button>
      </div>
    </>
  );
}

const HISTORY_STATUS: Record<string, string> = {
  completed: 'проведена',
  cancelled: 'отменена',
  no_show: 'клиент не пришёл',
  confirmed: 'подтверждена',
};

const historyStatus = (s: string) => HISTORY_STATUS[s] ?? s;

/**
 * Отвязка Telegram от карточки клиента.
 *
 * Ссылка-приглашение персональная, но живёт в переписке: по ней приходит второй
 * родственник, сосед по пересылке или бывший сотрудник — и карточка занята
 * чужим аккаунтом, а настоящее контактное лицо по ней уже не пустят. Отвязка — то
 * же, что «передать карточку» у специалиста: клиент, записи и история остаются на
 * месте, меняется только тот, кто входит и получает напоминания.
 *
 * Подтверждение и готовая ссылка живут в одной шторке — по той же причине, что и
 * у передачи карточки: мутация, открывшая следующую, потеряла бы свой колбэк
 * вместе со ссылкой, а прежний аккаунт к этому моменту уже отрезан.
 */
function UnlinkParentSheet({ family }: { family: Family }) {
  const sheet = useSheet();
  const toast = useToast();
  const unlink = useUnlinkClient();
  const [link, setLink] = useState<string | null>(null);

  if (link) {
    return (
      <>
        <h3>Аккаунт отвязан</h3>
        <p>
          Прежний аккаунт напоминаний больше не получит — мы предупредили его сообщением в боте.
          Отправьте ссылку {family.parent_name}: по ней он войдёт в ту же карточку, со всеми
          записями и расписанием.
        </p>
        <div className="field">
          <label htmlFor="unLink">Ссылка-приглашение</label>
          <input id="unLink" type="text" readOnly value={`https://${link}`} onFocus={(e) => e.currentTarget.select()} />
        </div>
        <button
          className="btn"
          onClick={() => {
            void navigator.clipboard?.writeText(`https://${link}`);
            toast('Ссылка скопирована');
          }}
        >
          Скопировать ссылку
        </button>
        <button className="btn solid" onClick={() => sheet.close()}>Готово</button>
      </>
    );
  }

  return (
    <>
      <h3>Отвязать Telegram от карточки?</h3>
      <p>
        Карточка {family.name.split(' ')[0]} останется прежней: записи, расписание и история
        никуда не денутся. Сменится только аккаунт, который входит в приложение и получает
        напоминания.
      </p>
      <Notice>
        Прежний аккаунт потеряет доступ сразу. Новую ссылку покажем следом — отправьте её сами:
        бот не может написать первым тому, кто его не открывал.
      </Notice>
      <button
        className="btn solid"
        disabled={unlink.isPending}
        onClick={() =>
          unlink.mutate(family.client_id, {
            onSuccess: (res) => setLink(res.link),
            onError: (e) => toast(e instanceof ApiError ? e.message : 'Не получилось отвязать аккаунт'),
          })
        }
      >
        {unlink.isPending ? 'Отвязываем…' : 'Отвязать и получить ссылку'}
      </button>
      <button className="btn" onClick={() => sheet.close()}>Отмена</button>
    </>
  );
}

/** Открепление отменяет будущие занятия — об этом предупреждаем до, а не после. */
function UnassignSheet({ family, member }: { family: Family; member: TeamMember }) {
  const sheet = useSheet();
  const toast = useToast();
  const { unassign } = useAssign();
  const first = member.specialist_name.split(' ')[0];

  return (
    <>
      <h3>Открепить {first} от {family.name.split(' ')[0]}?</h3>
      <p>Будущие записи к ней отменятся, клиент получит уведомление</p>
      <div className="row-actions">
        <button className="btn" onClick={() => sheet.close()}>Отмена</button>
        <button
          className="btn ghost-danger"
          disabled={unassign.isPending}
          onClick={() =>
            unassign.mutate(
              { childId: family.id, directionId: member.direction_id },
              {
                onSuccess: () => {
                  toast(`${first} откреплена от ${family.name.split(' ')[0]}`);
                  sheet.close();
                },
                onError: (e) => toast(e instanceof ApiError ? e.message : 'Не получилось'),
              },
            )
          }
        >
          Открепить
        </button>
      </div>
    </>
  );
}

/**
 * Закрепление показывает конфликты ДО того, как оно случилось.
 *
 * Клиента ведут несколько специалистов — по одному на категорию. Поэтому форма прежде
 * всего честно отвечает на вопрос «я добавляю человека к команде или меняю прежнего»:
 * в списке видно, кто какую категорию ведёт, а свободная категория выбрана по умолчанию.
 */
function AssignSheet({ family }: { family: Family }) {
  const sheet = useSheet();
  const toast = useToast();
  const me = useMeContext();
  const directions = useAdminDirections();
  const specialists = useAdminSpecialists();
  const { validate, assign } = useAssign();

  const dirs = (directions.data ?? []).filter((d) => d.is_active);
  /** Кто ведёт категорию. В одной категории специалист один — отсюда Map, а не список. */
  const occupied = new Map(family.team.map((t) => [t.direction_id, t]));
  const [directionId, setDirectionId] = useState<number | null>(null);
  /** По умолчанию первая свободная: специалиста чаще добавляют к команде, чем меняют. */
  const dirId = directionId ?? dirs.find((d) => !occupied.has(d.id))?.id ?? dirs[0]?.id ?? null;
  const options = (specialists.data ?? []).filter((s) => s.direction_ids.includes(dirId!) && s.status !== 'archived');
  const [specialistId, setSpecialistId] = useState<number | null>(null);
  const specId = specialistId ?? options[0]?.id ?? null;

  const taken = dirId === null ? undefined : occupied.get(dirId);
  const conflicts = validate.data?.conflicts ?? [];

  /** Помехи считаем и для категории по умолчанию: раньше их видел только тот, кто менял выбор руками. */
  const runValidate = validate.mutate;
  useEffect(() => {
    if (dirId !== null) runValidate({ childId: family.id, directionId: dirId });
  }, [runValidate, family.id, dirId]);

  return (
    <>
      <h3>{taken ? 'Заменить специалиста' : 'Закрепить специалиста'}</h3>
      <p>
        {taken
          ? `${family.name}. Категорию ведёт ${taken.specialist_name} — прежнее закрепление снимется.`
          : `${family.name}. Категория свободна: специалист добавится к команде, остальные останутся.`}
      </p>

      <div className="field">
        <label htmlFor="asDir">Категория</label>
        <select
          id="asDir"
          value={dirId ?? ''}
          onChange={(e) => {
            setDirectionId(Number(e.target.value));
            setSpecialistId(null);
          }}
        >
          {dirs.map((d) => {
            const busy = occupied.get(d.id);
            return (
              <option key={d.id} value={d.id}>
                {busy ? `${d.title} · ${busy.specialist_name}` : `${d.title} · свободна`}
              </option>
            );
          })}
        </select>
      </div>

      <div className="field">
        <label htmlFor="asSpec">Специалист</label>
        <select id="asSpec" value={specId ?? ''} onChange={(e) => setSpecialistId(Number(e.target.value))}>
          {options.length
            ? options.map((s) => (
                <option key={s.id} value={s.id}>{s.display_name}{s.status === 'paused' ? ' (пауза)' : ''}</option>
              ))
            : <option value="">В категории нет специалистов</option>}
        </select>
      </div>

      {conflicts.length ? (
        <Notice>
          {pluralWithCount(conflicts.length, 'помеха', 'помехи', 'помех')} к замене:
          у {taken?.specialist_name} остаются записи, ближайшая {formatDateTime(conflicts[0].starts_at_utc, me.center.tz, me.center.timeFormat)}
        </Notice>
      ) : null}

      <div className="row-actions">
        <button className="btn" onClick={() => sheet.close()}>Отмена</button>
        <button
          className="btn solid"
          disabled={!specId || !dirId || assign.isPending}
          onClick={() =>
            assign.mutate(
              { childId: family.id, directionId: dirId!, specialistId: specId! },
              {
                onSuccess: () => {
                  toast(taken ? 'Специалист заменён' : 'Специалист добавлен');
                  sheet.close();
                },
                onError: (e) => toast(e instanceof ApiError ? e.message : 'Не получилось закрепить'),
              },
            )
          }
        >
          {taken ? 'Заменить' : 'Добавить'}
        </button>
      </div>
    </>
  );
}

/** Подэкран «Новый клиент»: услуги уже получает, но бота не открывал. */
export function ClientAdd() {
  const me = useMeContext();
  const navigate = useNavigate();
  const toast = useToast();
  const sheet = useSheet();
  const directions = useAdminDirections();
  const specialists = useAdminSpecialists();
  const create = useCreateFamily();

  const dirs = (directions.data ?? []).filter((d) => d.is_active);
  const [childName, setChildName] = useState('');
  const [childAge, setChildAge] = useState('');
  const [note, setNote] = useState('');
  const [parent, setParent] = useState('');
  const [selfContact, setSelfContact] = useState(false);
  const [phone, setPhone] = useState('');
  const [directionId, setDirectionId] = useState<number | null>(null);
  const [specialistId, setSpecialistId] = useState<number | null>(null);

  const dirId = directionId ?? dirs[0]?.id ?? null;
  const options = (specialists.data ?? []).filter((s) => s.direction_ids.includes(dirId!) && s.status !== 'archived');
  const specId = specialistId ?? options[0]?.id ?? null;

  const nameOk = childName.trim().length > 1;
  const phoneOk = phone.replace(/\D/g, '').length >= 10;
  const contactName = selfContact ? childName : parent;
  // Специалист необязателен: без него карточка живёт на первичной услуге, закрепить можно позже.
  const ready = nameOk && contactName.trim().length > 0 && phoneOk;

  useScreen('Новый клиент', null);

  const submit = (force = false) => create.mutate(
    {
      childName: childName.trim(),
      birthYear: childAge ? new Date().getUTCFullYear() - Number(childAge) : null,
      note: note.trim(),
      parentName: contactName.trim(),
      phone: phone.trim(),
      directionId: dirId ?? undefined,
      specialistId: specId ?? undefined,
      force,
    },
    {
      onSuccess: (res) =>
        sheet.open(
          <FamilyCreatedSheet
            childName={childName.trim()}
            parentName={contactName.trim()}
            clientId={res.client.id}
            hasLink={!!res.inviteLink}
            seriesAsked={res.seriesRequested}
            adminContact={me.center.adminContact}
            onClose={() => navigate('/admin/clients')}
          />,
        ),
      onError: (e) => {
        if (e instanceof ApiError && e.is('CHILD_DUPLICATE')) {
          sheet.open(<DuplicateSheet message={e.message} onForce={() => submit(true)} />);
          return;
        }
        if (e instanceof NetworkError) {
          toast('Не сохранили — связь пропала. Данные в форме остались, попробуйте ещё раз');
          return;
        }
        toast(e instanceof ApiError ? e.message : 'Не получилось добавить клиента');
      },
    },
  );

  return (
    <Screen>
      <p className="lead">
        Для тех, кто уже ходит к вам, но бота ещё не открывал. Записывать можно сразу,
        приглашение в бот отправите после сохранения.
      </p>

      <p className="eyebrow" style={{ marginTop: 4 }}>Клиент</p>
      <div className="two">
        <div className="field">
          <label htmlFor="acName">Имя и фамилия</label>
          <input id="acName" type="text" placeholder="Кира Лебедева" value={childName} onChange={(e) => setChildName(e.target.value)} />
          {childName && !nameOk ? <span className="hint">Введите имя полностью, по нему карточку ищут в списке</span> : null}
        </div>
        <div className="field">
          <label htmlFor="acAge">Возраст</label>
          <input id="acAge" type="number" min="3" max="17" placeholder="8" inputMode="numeric" value={childAge} onChange={(e) => setChildAge(e.target.value)} />
        </div>
      </div>
      <div className="field">
        <label htmlFor="acReq">Запрос</label>
        <textarea id="acReq" placeholder="Дисграфия, трудности с письмом" value={note} onChange={(e) => setNote(e.target.value)} />
        <span className="hint">Видят специалисты, которые ведут клиента.</span>
      </div>

      <p className="eyebrow" style={{ marginTop: 4 }}>Контактное лицо</p>
      <label className="check">
        <input type="checkbox" checked={selfContact} onChange={(e) => setSelfContact(e.target.checked)} />
        <span>Контактное лицо — сам клиент</span>
      </label>
      {selfContact ? null : (
        <div className="field">
          <label htmlFor="acParent">Имя</label>
          <input id="acParent" type="text" placeholder="Ольга" value={parent} onChange={(e) => setParent(e.target.value)} />
        </div>
      )}
      <div className="field">
        <label htmlFor="acPhone">Телефон</label>
        <input id="acPhone" type="tel" placeholder="+7 900 000-00-00" value={phone} onChange={(e) => setPhone(e.target.value)} />
        <span className="hint">
          {phone && !phoneOk
            ? 'Нужно 11 цифр, как в примере: +7 900 000-00-00'
            : 'По нему свяжутся до подключения к боту и проверят, нет ли двойника.'}
        </span>
      </div>

      <p className="eyebrow" style={{ marginTop: 4 }}>Кто ведёт</p>
      <div className="field">
        <label htmlFor="acDir">Категория</label>
        <select
          id="acDir"
          value={dirId ?? ''}
          onChange={(e) => {
            setDirectionId(Number(e.target.value));
            setSpecialistId(null);
          }}
        >
          {dirs.map((d) => <option key={d.id} value={d.id}>{d.title}</option>)}
        </select>
      </div>
      <div className="field">
        <label htmlFor="acSp">Специалист</label>
        <select id="acSp" value={specId ?? ''} onChange={(e) => setSpecialistId(Number(e.target.value))}>
          {options.length
            ? options.map((s) => <option key={s.id} value={s.id}>{s.display_name}</option>)
            : <option value="">В этой категории пока нет специалистов</option>}
        </select>
      </div>
      {specId === null ? (
        <Notice>
          В этой категории пока некого закрепить. Карточку можно сохранить и так: до закрепления клиент
          сможет записаться только на первичную услугу.
        </Notice>
      ) : null}

      <button className="btn solid" disabled={!ready || create.isPending} onClick={() => submit()}>
        {create.isPending ? 'Добавляем…' : 'Добавить клиента'}
      </button>
    </Screen>
  );
}

/** Такой клиент с таким телефоном уже заведён — почти всегда его просто не нашли поиском. */
function DuplicateSheet({ message, onForce }: { message: string; onForce: () => void }) {
  const sheet = useSheet();
  const navigate = useNavigate();

  return (
    <>
      <h3>{message}</h3>
      <p>Скорее всего, карточку уже заводили. Двойник разведёт записи и расписание по двум карточкам.</p>
      <div className="row-actions">
        <button
          className="btn"
          onClick={() => {
            sheet.close();
            navigate('/admin/clients');
          }}
        >
          Открыть карточку
        </button>
        <button
          className="btn solid"
          onClick={() => {
            sheet.close();
            onForce();
          }}
        >
          Всё равно завести
        </button>
      </div>
    </>
  );
}

/**
 * Шаг после сохранения. Без него карточка, заведённая вручную, остаётся мёртвой
 * записью: контактное лицо о ней не знает, а сотрудник считает дело сделанным.
 */
function FamilyCreatedSheet({ childName, parentName, clientId, hasLink, seriesAsked, adminContact, onClose }: {
  childName: string;
  parentName: string;
  clientId: number;
  hasLink: boolean;
  /** Специалисту ушла просьба выставить расписание — администратору не нужно просить отдельно. */
  seriesAsked: boolean;
  adminContact: string;
  onClose: () => void;
}) {
  const sheet = useSheet();
  const toast = useToast();
  const invite = useInviteLink();

  const done = () => {
    sheet.close();
    onClose();
  };

  return (
    <>
      <h3>Карточка {firstName(childName)} готова</h3>
      <p>
        Осталось пригласить {parentName} — отправьте ссылку в мессенджер.
        По ней контактное лицо откроет бота и увидит расписание.
      </p>

      {seriesAsked ? <p>Специалисту ушла просьба выставить расписание.</p> : null}

      {hasLink ? null : (
        <Notice>
          У организации не настроен бот, поэтому ссылки пока нет. Напишите сами:{' '}
          {adminContact || 'контакт администратора не задан'}.
        </Notice>
      )}

      <div className="row-actions">
        <button className="btn" onClick={done}>Позже</button>
        <button
          className="btn solid"
          disabled={!hasLink || invite.isPending}
          onClick={() =>
            invite.mutate(clientId, {
              onSuccess: (res) => {
                void navigator.clipboard?.writeText(`https://${res.link}`);
                toast(`Ссылка скопирована — отправьте её ${parentName}`);
                done();
              },
              onError: () => toast('Не получилось получить ссылку'),
            })
          }
        >
          Скопировать приглашение
        </button>
      </div>
    </>
  );
}

const firstName = (full: string) => full.trim().split(/\s+/)[0];

function seriesLine(s: Series, tf: TimeFormat): string {
  const days = String(s.weekdays ?? '')
    .split(',')
    .map((x) => Number(x.trim()))
    .filter((n) => Number.isInteger(n))
    .map((d) => weekdayRu(d))
    .join(', ');
  return `${days} в ${formatMinutes(s.time_min, tf)}`;
}

function age(birthYear: number): string {
  const n = new Date().getUTCFullYear() - birthYear;
  return `${n} ${plural(n, 'год', 'года', 'лет')}`;
}
